import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, Search } from 'lucide-react';
import RenterLayout from '../components/layout/RenterLayout';
import { renterService } from '../services/renter.service';
import { buildPublicFileUrl } from '../utils/fileUrl';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&q=80&w=1200';

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return FALLBACK_IMAGE;
  return buildPublicFileUrl(imageUrl);
};

const getStatusClasses = (status) => {
  switch (status) {
    case 'SUCCESS':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'REFUNDED':
      return 'bg-amber-50 text-amber-700 border-amber-100';
    case 'FAILED':
      return 'bg-red-50 text-red-700 border-red-100';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

const RenterPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const loadPayments = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await renterService.getPayments();
        setPayments(data || []);
      } catch (err) {
        setError(err.message || 'Failed to load payments.');
      } finally {
        setLoading(false);
      }
    };

    loadPayments();
  }, []);

  const filteredPayments = payments.filter((payment) => {
    const text = `${payment.vehicle || ''} ${payment.bookingId || ''} ${payment.paymentMethod || ''} ${payment.status || ''}`.toLowerCase();
    return text.includes(query.toLowerCase());
  });

  return (
    <RenterLayout
      activePage="Payments"
      title="Payment History"
      description="Track completed charges, pending payment issues, and refunded transactions connected to your bookings."
      actions={
        <div className="relative w-full lg:w-80">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search payments"
            className="w-full rounded-2xl border border-gray-200 bg-white pl-11 pr-4 py-3 text-sm font-semibold outline-none focus:border-brand-black"
          />
        </div>
      }
    >
      {loading ? (
        <div className="min-h-[320px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="rounded-[1.75rem] border border-red-100 bg-red-50 px-6 py-5 text-red-700 font-semibold">
          {error}
        </div>
      ) : filteredPayments.length === 0 ? (
        <div className="rounded-[1.75rem] border border-gray-100 bg-[#f8fafc] px-8 py-14 text-center">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-black tracking-tight mb-2">No payments found</h2>
          <p className="text-gray-500 font-medium">Once you complete bookings, the related payments will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPayments.map((payment) => (
            <div key={payment.id} className="grid grid-cols-1 md:grid-cols-[190px_minmax(0,1fr)] gap-5 rounded-[1.75rem] border border-gray-100 bg-white p-5">
              <div className="h-44 rounded-[1.25rem] overflow-hidden bg-[#f8fafc]">
                <img src={getImageUrl(payment.image)} alt={payment.vehicle} className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col justify-between gap-5">
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-2">
                        Payment Ref {payment.id?.slice(0, 8)?.toUpperCase()}
                      </p>
                      <h2 className="text-2xl font-black tracking-tight">{payment.vehicle || 'Vehicle Payment'}</h2>
                    </div>
                    <span className={`rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-[0.18em] ${getStatusClasses(payment.status)}`}>
                      {payment.status || 'PENDING'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Amount</p>
                      <p className="text-sm font-black text-brand-black">{payment.amount || 'RF 0'}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Method</p>
                      <p className="text-sm font-semibold text-gray-600">{payment.paymentMethod || 'Not specified'}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Date</p>
                      <p className="text-sm font-semibold text-gray-600">{payment.date} {payment.time}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Booking</p>
                      <p className="text-sm font-semibold text-gray-600">{payment.bookingId?.slice(0, 8)?.toUpperCase() || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </RenterLayout>
  );
};

export default RenterPayments;
